<template>
  <div>
    <serveur-info :erreur="flags.erreur" />
    <my-notification v-if="authStore.isAuthenticated" :icon="'bicycle'">
      <template #title>
        {{ $t('compCarbonFormsCommutesMain.title') }}
      </template>
      <span v-html="$t('compCarbonFormsCommutesMain.info-1')" />
      <br />
      <span v-html="$t('compCarbonFormsCommutesMain.info-file-upload')" />
    </my-notification>
    <my-notification v-else :icon="'bicycle'">
      <template #title>
        {{ $t('compCarbonFormsCommutesMain.title') }}
      </template>
      <span v-html="$t('compCarbonFormsCommutesMain.info-2')"></span>
      <br />
      <br />
      {{ $t('compCarbonFormsCommutesMain.info-3') }}
    </my-notification>
    <br v-if="authStore.isAuthenticated" />
    <survey-config
      v-if="authStore.isAuthenticated"
      v-model="source"
      :submitted="isSubmitted"
      :module="module"
      :callback="callback"
      :formatDescription="formatDescription"
      withUpload
      @update="$emit('is-modified', true)"
    />

    <br />
    <covid-information :year="year" />
    <br v-if="year === 2020 || year === 2021" />

    <statistics-box />

    <commutes-table
      @edit="displayCommute"
      @update="$emit('is-modified', true)"
      @del="del"
      data-testid="commutes-table"
    />
    <br />
    <nav class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <div class="buttons">
          <b-button
            v-if="!authStore.isAuthenticated"
            :disabled="isSubmitted"
            class="button"
            icon-left="plus"
            @click="displayAddForm()"
            data-testid="commutes-add"
          >
            {{ $t('add') }}
          </b-button>
          <b-button
            v-if="authStore.isAuthenticated"
            class="level-item"
            icon-left="download"
            :disabled="nbDeplacements === 0"
            @click="exportCommutes"
          >
            {{ $t('export') }}
          </b-button>
          <b-button
            class="level-item"
            :icon-left="validSavedIcon"
            :disabled="!flags.isModified"
            type="is-primary"
            @click="validate"
            data-testid="commutes-save"
          >
            <span v-if="authStore.isAuthenticated">{{ $t('save') }}</span>
            <span v-else>{{ $t('validate') }}</span>
          </b-button>
        </div>
      </div>
    </nav>

    <b-modal
      :active.sync="displayModal"
      has-modal-card
      :can-cancel="['escape', 'x', 'outside']"
      :width="1000"
    >
      <div class="modal-card" style="width: 1000px">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('compCarbonFormsCommutesMain.label-3') }}
          </p>
        </header>
        <section class="modal-card-body">
          <survey-summary
            v-if="!modeCreation"
            :survey="commute"
            :isSimulation="false"
            :isSurvey="false"
            :year="year"
          />
          <br v-if="!modeCreation" />
          <b-notification
            v-if="!modeCreation"
            :closable="false"
            :has-icon="true"
            :icon="'envelope'"
          >
            <strong>{{ $t('compCarbonFormsCommutesMain.label-1') }}</strong>
            <br />
            <span v-if="commute.message">
              {{ commute.message }}
            </span>
            <span v-else>
              {{ $t('compCarbonFormsCommutesMain.label-2') }}
            </span>
          </b-notification>
          <survey-form
            v-show="modeCreation && !surveySubmitted && !commuteSubmitted"
            v-model="commute"
            :isSurvey="false"
            :year="year"
            :totalStep="totalStep"
            @submit="submitSurvey"
          />
          <commute-form
            v-show="modeCreation && surveySubmitted && !commuteSubmitted"
            v-model="commute"
            :isSurvey="false"
            :startStep="startStep"
            :totalStep="totalStep"
            :year="year"
            :hasWorkingDay2="hasWorkingDay2"
            :isSimulation="false"
            @updateHasWorkingDay2="updateHasWorkingDay2"
            @submit="add"
            @back="surveySubmitted = false"
          />
        </section>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { createExportFile } from '@/utils/files'
import Commute from '@/models/carbon/Commute.js'
import Modules from '@/models/Modules.js'
import { useAdminStore } from '@/stores/admin.js'
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useGHGIStore } from '@/stores/ghgi.js'
import { useCoreStore } from '@/stores/core.js'

import _ from 'lodash'

export default {
  name: 'Commutes',
  components: {
    'my-notification': () => import('@/components/MyNotification.vue'),
    'statistics-box': () =>
      import('@/components/carbon/forms/survey/StatisticsBox.vue'),
    'serveur-info': () => import('@/components/ServeurInfo.vue'),
    'survey-form': () => import('@/components/carbon/forms/survey/Form.vue'),
    'commute-form': () => import('@/components/carbon/forms/commutes/Form.vue'),
    'survey-summary': () =>
      import('@/components/carbon/forms/survey/Summary.vue'),
    'commutes-table': () =>
      import('@/components/carbon/forms/commutes/Table.vue'),
    'survey-config': () =>
      import('@/components/carbon/forms/survey/SurveyConfig.vue'),
    'covid-information': () =>
      import('@/components/carbon/forms/survey/CovidInformation.vue')
  },
  props: {
    flags: {
      type: Object
    }
  },
  data () {
    return {
      error: '',
      surveySubmitted: false,
      commuteSubmitted: false,
      hasWorkingDay2: false,
      displayModal: false,
      modeCreation: false,
      hasChanged: false,
      source: null,
      commute: Commute.createEmpty()
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const ghgiStore = useGHGIStore()
    const coreStore = useCoreStore()
    return { adminStore, coreStore, authStore, ghgiStore }
  },
  computed: {
    module: function () {
      return Modules.COMMUTES
    },
    validSavedIcon: function () {
      let icon = null
      if (this.authStore.isAuthenticated) {
        icon = 'save'
      } else {
        icon = 'check'
      }
      return icon
    },
    positionDic: function () {
      return this.coreStore.surveyPositionDictionary
    },
    positions () {
      return this.coreStore.surveyPositionTitles
    },
    formatDescription: function () {
      return Commute.formatDescription(this.positionDic)
    },
    startStep () {
      if (this.positions.length === 1) {
        return 2
      } else {
        return 3
      }
    },
    totalStep: function () {
      let totalStep = 8
      if (this.commute.nWorkingDay === 0) {
        totalStep = 1
      } else if (this.commute.nWorkingDay === 1) {
        totalStep = 4
      } else if (!this.hasWorkingDay2) {
        totalStep = 5
      } else {
        totalStep = 8
      }
      if (this.positions.length === 1) {
        totalStep -= 1
      }
      return totalStep
    },
    ghgi: function () {
      return this.ghgiStore.item
    },
    commutes: function () {
      return this.ghgiStore.items(Modules.COMMUTES)
    },
    year: function () {
      return this.ghgi.year
    },
    boundary: function () {
      return this.ghgi.boundary
    },
    nbValidDeplacements: function () {
      return this.commutes.filter((obj) => obj.isValid && !obj.cdeleted).length
    },
    nbDeplacements: function () {
      return this.commutes.filter((obj) => !obj.cdeleted).length
    },
    isSubmitted: function () {
      return this.ghgiStore.item.isSubmitted
    },
    isClonedSurvey: function () {
      return this.ghgiStore.isClonedSurvey
    }
  },
  methods: {
    updateHasWorkingDay2 (value) {
      this.hasWorkingDay2 = value
    },
    submitSurvey: function () {
      if (this.commute.nWorkingDay === 0) {
        this.add()
      } else {
        this.surveySubmitted = true
      }
    },
    exportCommutes: function () {
      this.ghgiStore.compute()
      let content = Commute.exportToFile(this.commutes)
      createExportFile(content, 'ghgi' + this.year + '_commutes')
    },
    closeModal: function () {
      this.displayModal = false
      this.commuteSubmitted = false
      this.surveySubmitted = false
      this.commute = Commute.createEmpty()
    },
    add: function () {
      this.commute = Commute.createFromObj(this.commute)
      this.commute.clean()
      this.ghgiStore.addItem({
        module: Modules.COMMUTES,
        item: this.commute
      })
      this.$emit('is-modified', true)
      this.closeModal()
    },
    displayAddForm: function () {
      let seqID = this.commutes.length + 1
      this.modeCreation = true
      this.commute = Commute.createEmpty()
      this.commute.seqID = 'u' + seqID
      this.displayModal = true
    },
    del: function (items) {
      this.$emit('is-modified', true)
      if (this.isEditable) {
        this.ghgiStore.deleteItems({
          module: this.module,
          items: items
        })
      } else {
        for (let item of items) {
          let nitem = _.cloneDeep(item)
          nitem.cdeleted = !item.cdeleted
          this.ghgiStore.updateItem({
            module: Modules.COMMUTES,
            item: {
              from: item,
              to: nitem
            }
          })
        }
      }
    },
    displayCommute: function (commute) {
      this.modeCreation = false
      this.commute = commute
      this.displayModal = true
    },
    callback: function (data, format, source) {
      let allTags = this.adminStore.allTagCategories
        .map((obj) => obj.tags)
        .flat()
      let newSurveys = Commute.createFromLines(data, format, allTags, source)
      for (let survey of newSurveys) {
        survey.clean()
        this.ghgiStore.addItem({
          module: this.module,
          item: survey
        })
      }
    },
    validate: function () {
      let self = this
      if (this.nbValidDeplacements !== this.nbDeplacements) {
        this.$buefy.dialog.confirm({
          title: self.$t('compCarbonFormsCommutesMain.validate-msg-1'),
          message: self.$t('compCarbonFormsCommutesMain.validate-msg-2'),
          confirmText: self.$t('validate'),
          cancelText: self.$t('cancel'),
          canCancel: ['escape', 'button', 'outside'],
          onConfirm: function () {
            self.subValidate()
          }
        })
      } else {
        this.subValidate()
      }
    },
    subValidate: function () {
      if (this.authStore.isAuthenticated) {
        const loadingComponent = this.$buefy.loading.open({
          container: null
        })
        this.ghgiStore
          .saveItems(Modules.COMMUTES)
          .then((data) => {
            loadingComponent.close()
            this.$emit('is-valid', true)
            this.error = ''
            this.$emit('is-modified', false)
            window.scrollTo(0, 0)
            this.$buefy.toast.open({
              type: 'is-light',
              message: this.$t('compCarbonFormsCommutesMain.save-msg-2')
            })
          })
          .catch((erreur) => {
            loadingComponent.close()
            this.$emit('is-valid', false)
            this.error = erreur.response.data
          })
      } else {
        this.$emit('is-valid', true)
        this.$emit('is-modified', false)
      }
    }
  }
}
</script>
